import computeScrollIntoView from 'compute-scroll-into-view';
import {useState, useEffect} from 'react';
import debounce from './debounce';

/**
 * Normalizes the 'key' property of a KeyboardEvent in IE/Edge
 * @param {Object} event a keyboardEvent object
 * @return {String} keyboard key
 */
export function normalizeArrowKey(event) {
  const {key, keyCode} = event;
  if (keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0) {
    return `Arrow${key}`;
  }
  return key;
}

export function handleRefs(...refs) {
  return node => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
}

export function scrollIntoView(node, menuNode) {
  if (node === null) {
    return;
  }

  const actions = computeScrollIntoView(node, {
    boundary: menuNode,
    block: 'nearest',
    scrollMode: 'if-needed',
  });
  actions.forEach(({el, top, left}) => {
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}

// 🐨 understand how it works before committing first commit
export function callAllEventHandlers(...fns) {
  return (event, ...args) =>
    fns.some(fn => {
      if (fn) {
        fn(event, ...args);
      }

      // 🐨 what the fuck is this
      // return (
      //   event.preventAutocompleteDefault ||
      //   (event.hasOwnProperty('nativeEvent') &&
      //     event.nativeEvent.preventAutocompleteDefault)
      // )
    });
}

// 🐨 revisit, could be better ? ( see Downshift.js implementation)
export function defaultItemToString(item) {
  return item ? String(item) : '';
}

export function capitalizeString(string) {
  return `${string.slice(0, 1).toUpperCase()}${string.slice(1)}`;
}

export function getA11ySelectionMessage({selectedItem, itemToString}) {
  if (!selectedItem) {
    return '';
  }

  return `${itemToString(selectedItem)} has been selected.`;
}

export function getA11yStatusMessage({isOpen, items}) {
  if (!items) {
    return '';
  }
  const resultCount = items.length;
  if (isOpen) {
    if (resultCount === 0) {
      return 'No results are available';
    }
    return `${resultCount} result${
      resultCount === 1 ? ' is' : 's are'
    } available, use up and down arrow keys to navigate. Press Enter key to select.`;
  }
  return '';
}

/**
 * @param {String} status the status message
 * @param {Object} documentProp document passed by the user.
 */
export function setStatus(status, documentProp) {
  const div = getStatusDiv(documentProp);

  // 🐨 ts-todo: redundant if this is not going to be exposed in public api
  if (!status) {
    return;
  }

  div.textContent = status;
  cleanupStatus();
}

export function cleanupStatus() {
  debounce(() => {
    getStatusDiv().textContent = '';
  }, 500);
}

export function getStatusDiv(documentProp = document) {
  let statusDiv = documentProp.getElementById('a11y-status-message');
  if (statusDiv) {
    return statusDiv;
  }

  statusDiv = documentProp.createElement('div');
  statusDiv.setAttribute('id', 'a11y-status-message');
  statusDiv.setAttribute('role', 'status');
  statusDiv.setAttribute('aria-live', 'polite');
  statusDiv.setAttribute('aria-relevant', 'additions text');
  Object.assign(statusDiv.style, {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px',
  });
  documentProp.body.appendChild(statusDiv);
  return statusDiv;
}

export function getState(state, props) {
  return Object.keys(state).reduce((stateAcc, key) => {
    stateAcc[key] =
      key in props // control prop is present
        ? props[key]
        : state[key];

    return stateAcc;
  }, {});
}

export function getElementIds(generateDefaultId, {id, labelId, menuId, getItemId, inputId} = {}) {
  const uniqueId = id === undefined ? `downshift-${generateDefaultId()}` : id;

  return {
    labelId: labelId || `${uniqueId}-label`,
    menuId: menuId || `${uniqueId}-menu`,
    getItemId: getItemId || (index => `${uniqueId}-item-${index}`),
    inputId: inputId || `${uniqueId}-input`,
  };
}

export function invokeOnChangeHandler(propKey, props, state, changes) {
  const handler = `on${capitalizeString(propKey)}Change`;
  if (props[handler] && changes[propKey] !== undefined && changes[propKey] !== state[propKey]) {
    // 🐨🗒️:
    /** a function called 'getStateAndHelpers'
     * can be used to pass all the state and utilities
     * to the onChange handlers if need be. Not needed for now
     */
    props[handler](changes);
  }
}

export function callOnChangeProps(props, state, changes) {
  Object.keys(state).forEach(stateKey => {
    invokeOnChangeHandler(stateKey, props, state, changes);
  });

  if (props.onStateChange && changes !== undefined) {
    props.onStateChange(changes);
  }
}

export function getItemIndex(index, item, items) {
  if (index !== undefined) {
    return index;
  }
  if (items.length === 0) {
    return -1;
  }
  return items.indexOf(item);
}

let lastId = 0;
// istanbul ignore next
export const genId = () => ++lastId;

/**
 * Autogenerate IDs to facilitate WAI-ARIA and server rendering.
 * Taken from @reach/auto-id
 * @see https://github.com/reach/reach-ui/blob/6e9dbcf716d5c9a3420e062e5bac1ac4671d01cb/packages/auto-id/src/index.js
 */
// istanbul ignore next
export function useId() {
  const [id, setId] = useState(null);

  useEffect(() => setId(genId()), []);

  return id;
}

export const defaultStateValues = {
  highlightedIndex: -1,
  isOpen: false,
  selectedItem: null,
  inputValue: '',
};

export function getDefaultValue(props, statePropKey) {
  const defaultPropKey = `default${capitalizeString(statePropKey)}`;
  if (defaultPropKey in props) {
    return props[defaultPropKey];
  }
  return defaultStateValues[statePropKey];
}

export function getInitialValue(props, statePropKey) {
  if (statePropKey in props) {
    return props[statePropKey];
  }
  const initialPropKey = `initial${capitalizeString(statePropKey)}`;
  if (initialPropKey in props) {
    return props[initialPropKey];
  }
  return getDefaultValue(props, statePropKey);
}

export function getInitialState(props) {
  const {initialInputValue, itemToString} = props;

  const selectedItem = getInitialValue(props, 'selectedItem');
  const highlightedIndex = getInitialValue(props, 'highlightedIndex');
  const isOpen = getInitialValue(props, 'isOpen');
  let inputValue = getInitialValue(props, 'inputValue');

  if (selectedItem !== null && initialInputValue === undefined) {
    inputValue = itemToString(selectedItem);
  }

  return {
    highlightedIndex:
      highlightedIndex < 0 && selectedItem
        ? // there is no 'items' prop coming 🐨 need to correct this.
          props.items.indexOf(selectedItem)
        : highlightedIndex,
    isOpen,
    selectedItem,
    inputValue,
  };
}

/**
 * @param {HTMLElement} parent the parent node
 * @param {HTMLElement} child the child node
 * @return {Boolean} whether the parent is the child or the child is in the parent
 */
export function isOrContainsNode(parent, child) {
  return parent === child || (parent.contains && parent.contains(child));
}
